import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    forwardRef,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewEncapsulation,
} from '@angular/core';
import {
    ControlValueAccessor,
    NG_VALUE_ACCESSOR,
    NgControl,
} from '@angular/forms';
import { MatFormFieldControl } from '@angular/material/form-field';
import { ISuggestValue } from '@uipath/angular/components/ui-suggest/models';
import { identifier } from '@uipath/angular/utilities';

import { Subject } from 'rxjs';

@Component({
    selector: 'ui-simple-multichoice-entry',
    templateUrl: './simple-multichoice-entry.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: MatFormFieldControl,
            useExisting: SimpleMultiChoiceEntryComponent,
        },
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => SimpleMultiChoiceEntryComponent),
            multi: true,
        },
    ],
    encapsulation: ViewEncapsulation.None,
})
export class SimpleMultiChoiceEntryComponent implements
    OnInit,
    OnDestroy,
    MatFormFieldControl<string | null>,
    ControlValueAccessor {

    @Input() possibleValues!: string[];
    @Input() defaultValue = null;
    @Input() labelText = 'Value';
    @Input() disabled = false;
    @Input() currentValue: string | null = null;

    public shouldLabelFloat = false;
    public id: string;
    public stateChanges = new Subject<void>();
    public ngControl: NgControl | null = null;
    public focused = false;
    public errorState = false;
    public describedBy = '';

    public get choices(): ISuggestValue[] {
        return this.possibleValues!.map(value => ({
            id: value,
            text: value,
        }));
    }

    @Output() optionChange = new EventEmitter<string | null>();

    private _randomName: string;
    private _required = false;

    constructor(
        private _cd: ChangeDetectorRef,
    ) {
        this._randomName = identifier();
        this.id = 'multi-choice-simple-' + this._randomName;
    }

    ngOnInit() {
        this.currentValue = this.defaultValue;
    }

    ngOnDestroy() {
        this.stateChanges.complete();
    }

    @Input()
    get value(): string | null {
        return this.currentValue;
    }
    set value(state: string | null) {
        this.currentValue = state;
        this.stateChanges.next();
    }

    @Input()
    get placeholder() {
        return this.labelText;
    }
    set placeholder(plh) {
        this.labelText = plh;
        this.stateChanges.next();
    }

    get empty() {
        return this.currentValue == null;
    }

    @Input()
    get required() {
        return this._required;
    }
    set required(req) {
        this._required = req;
        this.stateChanges.next();
    }

    writeValue(value: string | null) {
        this.currentValue = value;
        this._cd.markForCheck();
        this.stateChanges.next();
    }

    registerOnChange(fn: (ev: any) => void) {
        this._onChange = fn;
    }

    registerOnTouched(_: () => void) { }

    setDisabledState(isDisabled: boolean) {
        this.disabled = isDisabled;
        this._cd.detectChanges();
    }

    setDescribedByIds(ids: string[]) {
        this.describedBy = ids.join(' ');
    }

    onContainerClick(_event: MouseEvent) { }

    public onChoiceSelect(choice: ISuggestValue | null) {
        const choiceValueAsString = choice ? choice.id as string : null;

        this.writeValue(choiceValueAsString);
        this._onChange(choiceValueAsString);
        this.optionChange.emit(choiceValueAsString);
    }

    private _onChange = (_value: any) => { };
}
